import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import { useBackState, useScrollMemory } from "@/hooks/useScrollMemory";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PhotoThumbs } from "@/components/PhotoThumbs";
import { CheckCircle2, ClipboardCheck, Sparkles, Truck, Boxes } from "lucide-react";

// Admin Review Gate — parts an identifier completed (without finalize_without_review)
// wait here at pending_review for QA sign-off before going Available.
export default function ReviewQueuePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useBackState("page", 1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  useScrollMemory(!loading);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/review-queue", { params: { page: p, limit: 50 } });
      setItems(data.items || []);
      setTotal(data.total ?? data.count ?? 0);
      setPageSize(data.page_size || 50);
      setPage(data.page || p);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(1); }, []); // eslint-disable-line

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const goPage = (p) => { const np = Math.min(Math.max(1, p), lastPage); if (np !== page) { window.scrollTo(0, 0); load(np); } };

  // Reviewing now happens on the FULL part page (admin-review mode) — one editor, full
  // parity, no cramped window. Approve/Return/Fix-&-approve live there and return here.
  const openReview = (p) => navigate(`/sort-queue/${p.id}`, { state: fromState(location, "Admin Review") });

  return (
    <div data-testid="review-queue-page">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="w-6 h-6 text-[#B01E28]" />
        <h1 className="font-display text-2xl font-bold tracking-tight">Admin Review</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Completed parts awaiting QA sign-off before going Available. Approve, or return with feedback.{total > 0 && <span data-testid="review-queue-total" className="ml-1 font-mono text-slate-600">· {total} waiting</span>}</p>

      {loading ? (
        <div className="font-mono text-xs text-slate-400 py-10">LOADING…</div>
      ) : items.length === 0 ? (
        <div data-testid="review-queue-empty" className="text-center py-16 text-slate-400">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />Nothing waiting for review.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`review-card-${p.short_code}`} data-back-row className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-mono font-bold text-sm">{p.short_code}</div>
                  <div className="text-sm text-slate-700">{p.name || <span className="text-slate-400 italic">Unnamed</span>} {p.brand ? `· ${p.brand}` : ""}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {p.ai_assisted && <Badge className="rounded-full border-0 bg-violet-100 text-violet-700 text-[10px]"><Sparkles className="w-3 h-3 mr-0.5" />AI-assisted</Badge>}
                  <Badge className="rounded-full border-0 bg-slate-100 text-slate-500 text-[10px] flex items-center gap-1">{p.source_type === "donor" ? <Truck className="w-3 h-3" /> : <Boxes className="w-3 h-3" />}{p.source_label}</Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2 text-[11px]">
                {p.grade && <Badge className="rounded-full border-0 bg-slate-800 text-white">Grade {p.grade}</Badge>}
                {p.ai_intents?.map((i) => <Badge key={i} className="rounded-full border border-violet-200 bg-violet-50 text-violet-600">{i}</Badge>)}
                <span className="text-slate-400">by {(p.completed_by || "").split("@")[0]}</span>
              </div>
              {/* REVIEWING IS LOOKING. The thumbs open the one viewer (2026-10-15) — they used
                  to be dead squares on the screen whose whole job is checking somebody's work. */}
              {p.photos?.length > 0 && (
                <div className="mt-3">
                  <PhotoThumbs photos={p.photos} max={5} size="w-14 h-14" label="part photo"
                    testidPrefix={`review-photos-${p.short_code}`}
                    fixScope="part" fixRecordId={p.id} onFixed={load} />
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button data-testid={`review-open-${p.short_code}`} size="sm" className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white" onClick={() => openReview(p)}>
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Review full part
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && total > pageSize && (
        <div data-testid="review-queue-pager" className="flex items-center justify-center gap-3 mt-6">
          <Button data-testid="review-prev-page" size="sm" variant="outline" className="rounded-lg" disabled={page <= 1} onClick={() => goPage(page - 1)}>Previous</Button>
          <span className="font-mono text-xs text-slate-500">Page {page} of {lastPage}</span>
          <Button data-testid="review-next-page" size="sm" variant="outline" className="rounded-lg" disabled={page >= lastPage} onClick={() => goPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
